import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of, throwError, timer } from "rxjs";
import { catchError, map, switchMap, tap, timeout } from "rxjs/operators";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";
import { nativeAppUrl } from "../utils/native-app-url.utils";
import {
  GuestOwnerClaimResponse,
  GuestOwnerCurrentEventResponse,
  GuestOwnerSessionState,
  PublicEventRsvpResponseType,
  PublicEventInvitePreviewResponse,
  PublicInviteParticipantClaimResponse
} from "../models/guestOwner";
import { GuestOwnerSessionService } from "./guest-owner-session.service";
import { clearGuestIdentityBrowserState } from "./guest-identity-storage";

export interface PublicInviteParticipantClaimState {
  inviteToken: string;
  participantId: string;
  guestParticipantToken: string;
  eventId: string;
  name?: string;
  response?: PublicEventRsvpResponseType;
  updatedAt: string;
}

export interface GuestUpgradeClaimResult {
  attempted: boolean;
  success: boolean;
  type?: "guest-owner" | "public-invite";
  eventId?: string;
  message?: string;
  error?: any;
  staleStateCleared?: boolean;
}

export interface GuestClaimRecovery {
  key: string;
  eventName?: string;
  reason: "ready" | "unavailable" | "expired" | "otherAccount" | "busy" | "connection";
  canClaim: boolean;
}

export type GuestUpgradeStatus = "PENDING_AUTH" | "COMPLETING" | "COMPLETED" | "CANCELLED" | "EXPIRED";
export type GuestUpgradeType = "GUEST_OWNER" | "PUBLIC_INVITE_PARTICIPANT";

export interface GuestUpgradeTransaction {
  linkedMatchReference?: string;
  transitionId?: string;
  upgradeToken?: string;
  type: GuestUpgradeType;
  status: GuestUpgradeStatus;
  eventId: string;
  eventName?: string;
  guestPlayerId?: string;
  playerId?: string;
  attemptCount: number;
  lastFailureCode?: string;
  expiresAt: string;
  completedAt?: string;
  retryable: boolean;
  readOnlyHandoff?: boolean;
  destination?: "HOME" | "MATCH";
  updatedAt?: string;
  terminal?: boolean;
  guestAccessRevoked?: boolean;
}

export interface GuestUpgradeHandoff {
  handoffCode: string;
  expiresAt: string;
}

export interface GuestContinuationHandoff {
  handoffCode: string;
  observerToken: string;
  expiresAt: string;
}

export type GuestContinuationStatusType = "PENDING" | "OPENED" | "CLAIMED" | "REVOKED" | "EXPIRED";

export interface GuestContinuationStatus {
  linkedMatchReference?: string;
  status: GuestContinuationStatusType;
  type: GuestUpgradeType;
  eventId: string;
  eventName?: string;
  expiresAt?: string;
  redeemedAt?: string;
  destination?: "HOME" | "MATCH";
  terminal?: boolean;
  guestAccessRevoked?: boolean;
}

interface GuestContinuationObserverState {
  observerToken: string;
  type: GuestUpgradeType;
  eventId: string;
  expiresAt?: string;
  status?: GuestContinuationStatusType;
}

@Injectable({
  providedIn: "root"
})
export class GuestUpgradeClaimService {
  private static readonly PUBLIC_INVITE_CLAIMS_KEY = "nogosh.publicInvite.participantClaims";
  private static readonly UPGRADE_TOKEN_KEY = "nogosh.guestUpgrade.token";
  private static readonly COMPLETED_UPGRADE_KEY = "nogosh.guestUpgrade.completed";
  private static readonly CONTINUATION_OBSERVERS_KEY = "nogosh.guestContinuation.observers";
  private readonly guestOwnerApiUrl = environment.API_BASE_URL + "/guest-owner";
  private readonly publicInviteApiUrl = environment.API_BASE_URL + "/public-event-invite";
  private readonly guestUpgradeApiUrl = environment.API_BASE_URL + "/guest-upgrades";
  private readonly guestContinuationApiUrl = environment.API_BASE_URL + "/guest-continuations";

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private guestOwnerSessionService: GuestOwnerSessionService,
    private router: Router,
    private translate: TranslateService
  ) {}

  public savePublicInviteParticipantClaim(state: PublicInviteParticipantClaimState): void {
    if (!state || !state.inviteToken || !state.participantId || !state.guestParticipantToken) {
      return;
    }

    const claims = this.readPublicInviteClaims();
    claims[state.inviteToken] = Object.assign({}, state, {
      updatedAt: new Date().toISOString()
    });
    this.writePublicInviteClaims(claims);
  }

  public hasPendingClaim(): boolean {
    if (this.pendingUpgradeToken()) {
      return true;
    }
    const guestOwnerSession = this.guestOwnerSessionService.getSession();
    return !!(guestOwnerSession && guestOwnerSession.guestOwnerToken) || !!this.firstPublicInviteClaim();
  }

  public hasPublicInviteParticipantClaim(inviteToken: string): boolean {
    return this.isValidPublicInviteClaim(this.readPublicInviteClaims()[inviteToken]);
  }

  // Read-only inspection for ordinary login. Never transfer guest state here.
  public inspectPendingClaim(playerId: string): Observable<GuestClaimRecovery> {
    const token = this.pendingUpgradeToken();
    const session = this.guestOwnerSessionService.getSession();
    const claim = this.firstPublicInviteClaim();
    if (!token) {
      if (session && session.guestOwnerToken) {
        return this.http.get<GuestOwnerCurrentEventResponse>(`${this.guestOwnerApiUrl}/current-event`, {
          headers: new HttpHeaders({ "X-Guest-Owner-Token": session.guestOwnerToken })
        }).pipe(timeout(15000), map((response) => response && response.event ? {
          key: `owner:${response.event.id}`, eventName: response.event.name || session.eventName,
          reason: "ready" as const, canClaim: true
        } : null), catchError((error) => of(this.recoveryError(error, `owner:${session.eventId || session.player?.id}`))));
      }
      if (!claim) return of(null);
      return this.http.get<PublicEventInvitePreviewResponse>(`${this.publicInviteApiUrl}/${encodeURIComponent(claim.inviteToken)}`)
        .pipe(timeout(15000), map((response) => ({ key: `participant:${claim.participantId}`,
          eventName: response.name, reason: response.inviteActive ? "ready" as const : "unavailable" as const,
          canClaim: response.inviteActive === true })),
          catchError((error) => of(this.recoveryError(error, `participant:${claim.participantId}`))));
    }
    return this.http.get<GuestUpgradeTransaction>(`${this.guestUpgradeApiUrl}/current`, {
      headers: new HttpHeaders({ "X-Guest-Upgrade-Token": token })
    }).pipe(timeout(15000), map((transaction): GuestClaimRecovery => {
      if (transaction.status === "COMPLETED" && transaction.playerId === playerId) {
        this.rememberCompletedUpgrade(transaction);
        this.clearCompletedLocalState(transaction);
        return null;
      }
      const reason = transaction.playerId && transaction.playerId !== playerId ? "otherAccount"
        : transaction.status === "EXPIRED" ? "expired"
        : transaction.status === "COMPLETING" ? "busy"
        : transaction.status === "PENDING_AUTH" ? "ready" : "unavailable";
      return { key: `upgrade:${transaction.transitionId || transaction.eventId}`, eventName: transaction.eventName,
        reason, canClaim: reason === "ready" };
    }), catchError((error) => of(this.recoveryError(error, "upgrade:unavailable"))));
  }

  public recoveryDecision(key: string): string {
    try { return JSON.parse(localStorage.getItem("nogosh.guestUpgrade.decisions") || "{}")[key] || ""; }
    catch (_) { return ""; }
  }

  public saveRecoveryDecision(key: string, decision: "later" | "forgotten"): void {
    try {
      const decisions = JSON.parse(localStorage.getItem("nogosh.guestUpgrade.decisions") || "{}");
      decisions[key] = decision;
      localStorage.setItem("nogosh.guestUpgrade.decisions", JSON.stringify(decisions));
    } catch (_) {}
  }

  private recoveryError(error: any, key: string): GuestClaimRecovery {
    return { key, reason: this.isTerminalPrivateTokenError(error) ? "unavailable" : "connection", canClaim: false };
  }

  public claimPendingGuestState(navigateOnSuccess: boolean = true): Observable<GuestUpgradeClaimResult> {
    if (this.pendingUpgradeToken()) {
      return this.completePendingUpgrade().pipe(
        map((transaction) => {
          if (transaction.status !== "COMPLETED") {
            return {
              attempted: true,
              success: false,
              eventId: transaction.eventId,
              message: this.translate.instant("guestUpgrade.errors.moveGuestStateFailed")
            };
          }
          this.rememberCompletedUpgrade(transaction);
          this.clearCompletedLocalState(transaction);
          if (navigateOnSuccess) {
            this.navigateToAuthenticatedMatch(transaction.eventId);
          }
          return {
            attempted: true,
            success: true,
            type: transaction.type === "GUEST_OWNER" ? "guest-owner" as "guest-owner" : "public-invite" as "public-invite",
            eventId: transaction.eventId
          };
        }),
        catchError((error) => of(this.failedTransactionResult(error)))
      );
    }

    const guestOwnerSession = this.guestOwnerSessionService.getSession();
    if (guestOwnerSession && guestOwnerSession.guestOwnerToken) {
      return this.claimGuestOwnerMatch(guestOwnerSession).pipe(
        catchError((error) => of(this.failedResult("guest-owner", error)))
      );
    }

    const publicInviteClaim = this.firstPublicInviteClaim();
    if (publicInviteClaim) {
      return this.claimPublicInviteParticipant(publicInviteClaim).pipe(
        catchError((error) => of(this.failedResult("public-invite", error)))
      );
    }

    return of({ attempted: false, success: false });
  }

  public prepareGuestOwnerUpgrade(eventId: string): Observable<GuestUpgradeTransaction> {
    const session = this.guestOwnerSessionService.getSession();
    if (!session || !session.guestOwnerToken || !eventId) {
      return of(null);
    }
    this.clearCompletedUpgradeRecovery();
    return this.http.post<GuestUpgradeTransaction>(
      `${this.guestUpgradeApiUrl}/guest-owner/events/${encodeURIComponent(eventId)}`,
      { clientType: "WEB" },
      {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
          "X-Guest-Owner-Token": session.guestOwnerToken
        })
      }
    ).pipe(map((transaction) => this.savePreparedTransaction(transaction)));
  }

  public preparePublicInviteUpgrade(inviteToken: string): Observable<GuestUpgradeTransaction> {
    const claim = this.readPublicInviteClaims()[inviteToken];
    if (!this.isValidPublicInviteClaim(claim)) {
      return of(null);
    }
    this.clearCompletedUpgradeRecovery();
    return this.http.post<GuestUpgradeTransaction>(
      `${this.guestUpgradeApiUrl}/public-invite/${encodeURIComponent(inviteToken)}/participants/${encodeURIComponent(claim.participantId)}`,
      { clientType: "WEB" },
      {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
          "X-Guest-Participant-Token": claim.guestParticipantToken
        })
      }
    ).pipe(
      map((transaction) => this.savePreparedTransaction(transaction)),
      catchError((error) => {
        if (this.isTerminalPrivateTokenError(error)) {
          this.clearPublicInviteParticipantClaim(inviteToken);
        }
        return throwError(error);
      })
    );
  }

  public pendingUpgradeToken(): string {
    try {
      return localStorage.getItem(GuestUpgradeClaimService.UPGRADE_TOKEN_KEY) || "";
    } catch (_) {
      return "";
    }
  }

  public pendingUpgradeStatus(): Observable<GuestUpgradeTransaction> {
    const token = this.pendingUpgradeToken();
    if (!token) {
      return of(null);
    }
    return this.http.get<GuestUpgradeTransaction>(`${this.guestUpgradeApiUrl}/current`, {
      headers: new HttpHeaders({ "X-Guest-Upgrade-Token": token })
    }).pipe(
      map((transaction) => {
        if (transaction && transaction.status === "COMPLETED") {
          this.rememberCompletedUpgrade(transaction);
          this.clearCompletedLocalState(transaction);
        }
        return transaction;
      }),
      catchError((error) => {
        if (this.isTerminalPrivateTokenError(error)) {
          this.clearPendingUpgrade();
        }
        return throwError(error);
      })
    );
  }

  public completePendingUpgrade(): Observable<GuestUpgradeTransaction> {
    const token = this.pendingUpgradeToken();
    if (!token) {
      return of(null);
    }
    return this.auth.getAccessTokenSilently().pipe(
      timeout(15000),
      switchMap((accessToken) => this.http.post<GuestUpgradeTransaction>(
        `${this.guestUpgradeApiUrl}/complete`,
        { upgradeToken: token },
        this.claimOptions(accessToken, "X-Guest-Upgrade-Token", token)
      ).pipe(timeout(15000))),
      switchMap((transaction) => this.awaitCompletedTransaction(token, transaction))
    );
  }

  private awaitCompletedTransaction(token: string, transaction: GuestUpgradeTransaction,
                                    checksRemaining: number = 60): Observable<GuestUpgradeTransaction> {
    if (!transaction || transaction.status !== "COMPLETING" || checksRemaining <= 0) {
      return of(transaction);
    }
    return timer(500).pipe(
      switchMap(() => this.http.get<GuestUpgradeTransaction>(`${this.guestUpgradeApiUrl}/current`, {
        headers: new HttpHeaders({ "X-Guest-Upgrade-Token": token })
      })),
      switchMap((current) => this.awaitCompletedTransaction(token, current, checksRemaining - 1))
    );
  }

  public issueHandoff(): Observable<GuestUpgradeHandoff> {
    const token = this.pendingUpgradeToken();
    if (!token) {
      return of(null);
    }
    return this.http.post<GuestUpgradeHandoff>(`${this.guestUpgradeApiUrl}/handoff`, null, {
      headers: new HttpHeaders({ "X-Guest-Upgrade-Token": token })
    });
  }

  public redeemHandoff(handoffCode: string): Observable<GuestUpgradeTransaction> {
    return this.http.post<GuestUpgradeTransaction>(`${this.guestUpgradeApiUrl}/handoff/redeem`, {
      handoffCode
    }).pipe(map((transaction) => this.savePreparedTransaction(transaction)));
  }

  public issueGuestOwnerContinuation(eventId: string): Observable<GuestContinuationHandoff> {
    const session = this.guestOwnerSessionService.getSession();
    if (!session || !session.guestOwnerToken || !eventId) {
      return throwError(new Error("Guest owner session is missing."));
    }
    return this.http.post<GuestContinuationHandoff>(
      `${this.guestContinuationApiUrl}/guest-owner/events/${encodeURIComponent(eventId)}`,
      null,
      { headers: new HttpHeaders({ "X-Guest-Owner-Token": session.guestOwnerToken }) }
    ).pipe(tap((handoff) => this.rememberContinuationObserver(handoff, "GUEST_OWNER", eventId)));
  }

  public issuePublicInviteContinuation(inviteToken: string): Observable<GuestContinuationHandoff> {
    const claim = this.readPublicInviteClaims()[inviteToken];
    if (!this.isValidPublicInviteClaim(claim)) {
      return throwError(new Error("Guest participant session is missing."));
    }
    return this.http.post<GuestContinuationHandoff>(
      `${this.guestContinuationApiUrl}/public-invite/${encodeURIComponent(inviteToken)}/participants/${encodeURIComponent(claim.participantId)}`,
      null,
      { headers: new HttpHeaders({ "X-Guest-Participant-Token": claim.guestParticipantToken }) }
    ).pipe(tap((handoff) => this.rememberContinuationObserver(
      handoff,
      "PUBLIC_INVITE_PARTICIPANT",
      claim.eventId
    )));
  }

  public continuationStatus(type: GuestUpgradeType, eventId: string): Observable<GuestContinuationStatus> {
    const observer = this.continuationObserver(type, eventId);
    if (!observer || !observer.observerToken) {
      return of(null);
    }
    return this.http.get<GuestContinuationStatus>(`${this.guestContinuationApiUrl}/current`, {
      headers: new HttpHeaders({
        "X-Guest-Continuation-Observer-Token": observer.observerToken
      })
    }).pipe(
      tap((status) => this.rememberContinuationStatus(observer, status)),
      catchError((error) => {
        if (this.isTerminalPrivateTokenError(error)) {
          this.clearContinuationObserver(type, eventId);
        }
        return throwError(error);
      })
    );
  }

  public continuationWasClaimed(type: GuestUpgradeType, eventId: string): boolean {
    const observer = this.continuationObserver(type, eventId);
    return !!observer && observer.status === "CLAIMED";
  }

  public claimedMatchAppUrl(type: GuestUpgradeType, eventId: string,
                           transaction?: GuestUpgradeTransaction): Observable<string> {
    const completed = transaction || this.recentCompletedUpgrade();
    if (completed && completed.status === "COMPLETED" && completed.eventId === eventId
        && completed.type === type) {
      // Cached older responses already contain the server's transition id.
      const reference = completed.linkedMatchReference
        || (completed.transitionId ? `upgrade:${completed.transitionId}` : "");
      if (reference) {
        return of(nativeAppUrl(`linked-match?reference=${encodeURIComponent(reference)}`));
      }
    }
    return this.continuationStatus(type, eventId).pipe(map((status) => {
      if (!status || status.status !== "CLAIMED" || !status.linkedMatchReference) {
        throw new Error("Linked match reference is unavailable.");
      }
      return nativeAppUrl(`linked-match?reference=${encodeURIComponent(status.linkedMatchReference)}`);
    }));
  }

  public hasContinuationObserver(type: GuestUpgradeType, eventId: string): boolean {
    return !!this.continuationObserver(type, eventId);
  }

  public clearPendingUpgrade(): void {
    try {
      localStorage.removeItem(GuestUpgradeClaimService.UPGRADE_TOKEN_KEY);
    } catch (_) {
    }
  }

  public clearAllTransitionState(): void {
    clearGuestIdentityBrowserState();
  }

  public wasRevokedByAccountDeletion(transaction: GuestUpgradeTransaction): boolean {
    return !!transaction && transaction.status === "CANCELLED"
      && transaction.lastFailureCode === "ACCOUNT_DELETED";
  }

  private continuationObserverKey(type: GuestUpgradeType, eventId: string): string {
    return `${type}:${eventId}`;
  }

  private readContinuationObservers(): {[key: string]: GuestContinuationObserverState} {
    try {
      const rawObservers = localStorage.getItem(GuestUpgradeClaimService.CONTINUATION_OBSERVERS_KEY);
      return rawObservers ? JSON.parse(rawObservers) : {};
    } catch (_) {
      return {};
    }
  }

  private writeContinuationObservers(observers: {[key: string]: GuestContinuationObserverState}): void {
    try {
      localStorage.setItem(GuestUpgradeClaimService.CONTINUATION_OBSERVERS_KEY, JSON.stringify(observers));
    } catch (_) {
    }
  }

  private continuationObserver(type: GuestUpgradeType, eventId: string): GuestContinuationObserverState {
    if (!type || !eventId) {
      return null;
    }
    return this.readContinuationObservers()[this.continuationObserverKey(type, eventId)] || null;
  }

  private rememberContinuationObserver(handoff: GuestContinuationHandoff, type: GuestUpgradeType,
                                       eventId: string): void {
    if (!handoff || !handoff.observerToken || !type || !eventId) {
      return;
    }
    const observers = this.readContinuationObservers();
    observers[this.continuationObserverKey(type, eventId)] = {
      observerToken: handoff.observerToken,
      type,
      eventId,
      expiresAt: handoff.expiresAt,
      status: "PENDING"
    };
    this.writeContinuationObservers(observers);
  }

  private rememberContinuationStatus(observer: GuestContinuationObserverState,
                                     status: GuestContinuationStatus): void {
    if (!observer || !status) {
      return;
    }
    const observers = this.readContinuationObservers();
    observers[this.continuationObserverKey(observer.type, observer.eventId)] = Object.assign({}, observer, {
      status: status.status
    });
    this.writeContinuationObservers(observers);
  }

  private clearContinuationObserver(type: GuestUpgradeType, eventId: string): void {
    const observers = this.readContinuationObservers();
    delete observers[this.continuationObserverKey(type, eventId)];
    this.writeContinuationObservers(observers);
  }

  public clearCompletedUpgradeState(transaction: GuestUpgradeTransaction): void {
    if (!transaction || transaction.status !== "COMPLETED") {
      return;
    }
    this.clearCompletedLocalState(transaction);
  }

  public recentCompletedUpgrade(): GuestUpgradeTransaction {
    try {
      const rawTransaction = localStorage.getItem(GuestUpgradeClaimService.COMPLETED_UPGRADE_KEY);
      const transaction = rawTransaction ? JSON.parse(rawTransaction) as GuestUpgradeTransaction : null;
      const expiresAt = transaction && transaction.expiresAt ? new Date(transaction.expiresAt).getTime() : 0;
      if (!transaction || transaction.status !== "COMPLETED" || !transaction.eventId || !transaction.type
          || !expiresAt || expiresAt <= Date.now()) {
        this.clearCompletedUpgradeRecovery();
        return null;
      }
      return transaction;
    } catch (_) {
      this.clearCompletedUpgradeRecovery();
      return null;
    }
  }

  public clearCompletedUpgradeRecovery(): void {
    try {
      localStorage.removeItem(GuestUpgradeClaimService.COMPLETED_UPGRADE_KEY);
    } catch (_) {
    }
  }

  public claimPublicInviteParticipantForToken(inviteToken: string): Observable<GuestUpgradeClaimResult> {
    const claim = this.readPublicInviteClaims()[inviteToken];
    if (!this.isValidPublicInviteClaim(claim)) {
      return of({ attempted: false, success: false });
    }

    return this.claimPublicInviteParticipant(claim).pipe(
      catchError((error) => of(this.failedResult("public-invite", error)))
    );
  }

  private claimGuestOwnerMatch(session: GuestOwnerSessionState): Observable<GuestUpgradeClaimResult> {
    return this.resolveGuestOwnerEventId(session).pipe(
      switchMap((eventId) => {
        if (!eventId) {
          this.guestOwnerSessionService.clearSession();
          return of({
            attempted: true,
            success: false,
            type: "guest-owner" as "guest-owner",
            message: "We could not find the guest match on this device. The saved guest manager recovery state was cleared.",
            staleStateCleared: true
          });
        }

        return this.auth.getAccessTokenSilently().pipe(
          timeout(15000),
          switchMap((token) => this.http.post<GuestOwnerClaimResponse>(
            `${this.guestOwnerApiUrl}/events/${encodeURIComponent(eventId)}/claim`,
            null,
            this.claimOptions(token, "X-Guest-Owner-Token", session.guestOwnerToken)
          ).pipe(
            timeout(15000),
            map((response) => {
              const claimedEventId = response && (response.matchId || response.eventId) || eventId;
              this.guestOwnerSessionService.clearSession();
              this.navigateToAuthenticatedMatch(claimedEventId);
              return {
                attempted: true,
                success: true,
                type: "guest-owner" as "guest-owner",
                eventId: claimedEventId
              };
            })
          ))
        );
      })
    );
  }

  private claimPublicInviteParticipant(state: PublicInviteParticipantClaimState): Observable<GuestUpgradeClaimResult> {
    return this.auth.getAccessTokenSilently().pipe(
      timeout(15000),
      switchMap((token) => this.http.post<PublicInviteParticipantClaimResponse>(
        `${this.publicInviteApiUrl}/${encodeURIComponent(state.inviteToken)}/participants/${encodeURIComponent(state.participantId)}/claim`,
        null,
        this.claimOptions(token, "X-Guest-Participant-Token", state.guestParticipantToken)
      ).pipe(
        timeout(15000),
        map((response) => {
          const claimedEventId = response && response.eventId || state.eventId;
          this.clearPublicInviteParticipantClaim(state.inviteToken);
          this.navigateToAuthenticatedMatch(claimedEventId);
          return {
            attempted: true,
            success: true,
            type: "public-invite" as "public-invite",
            eventId: claimedEventId
          };
        })
      ))
    );
  }

  private resolveGuestOwnerEventId(session: GuestOwnerSessionState): Observable<string> {
    return this.http.get<GuestOwnerCurrentEventResponse>(
      `${this.guestOwnerApiUrl}/current-event`,
      {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
          "X-Guest-Owner-Token": session.guestOwnerToken
        })
      }
    ).pipe(
      timeout(15000),
      map((response) => response && response.event && response.event.id || "")
    );
  }

  private claimOptions(accessToken: string, privateTokenHeader: string, privateToken: string) {
    return {
      headers: new HttpHeaders({
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        [privateTokenHeader]: privateToken
      })
    };
  }

  private navigateToAuthenticatedMatch(eventId: string): void {
    localStorage.removeItem("event");
    this.router.navigate(["/event-players", false, { eventId }]);
  }

  private savePreparedTransaction(transaction: GuestUpgradeTransaction): GuestUpgradeTransaction {
    if (transaction && transaction.upgradeToken) {
      localStorage.setItem(GuestUpgradeClaimService.UPGRADE_TOKEN_KEY, transaction.upgradeToken);
      // An explicit new upgrade must not inherit an old dismissal.
      try {
        const decisions = JSON.parse(localStorage.getItem("nogosh.guestUpgrade.decisions") || "{}");
        delete decisions[`upgrade:${transaction.transitionId || transaction.eventId}`];
        delete decisions["upgrade:unavailable"];
        localStorage.setItem("nogosh.guestUpgrade.decisions", JSON.stringify(decisions));
      } catch (_) {}
    }
    return transaction;
  }

  private clearCompletedLocalState(transaction: GuestUpgradeTransaction): void {
    this.clearPendingUpgrade();
    if (transaction.type === "GUEST_OWNER") {
      this.guestOwnerSessionService.clearSession();
      return;
    }
    const claim = this.firstPublicInviteClaim();
    if (claim) {
      this.clearPublicInviteParticipantClaim(claim.inviteToken);
    }
  }

  private rememberCompletedUpgrade(transaction: GuestUpgradeTransaction): void {
    if (!transaction || transaction.status !== "COMPLETED") {
      return;
    }
    const safeTransaction = Object.assign({}, transaction);
    delete safeTransaction.upgradeToken;
    try {
      localStorage.setItem(GuestUpgradeClaimService.COMPLETED_UPGRADE_KEY, JSON.stringify(safeTransaction));
    } catch (_) {
    }
  }

  private failedTransactionResult(error: any): GuestUpgradeClaimResult {
    const status = error instanceof HttpErrorResponse || error && error.status ? error.status : null;
    if (status === 403 || status === 404 || status === 409) {
      return {
        attempted: true,
        success: false,
        message: this.translate.instant("guestUpgrade.errors.recoveryRequired"),
        error
      };
    }
    return {
      attempted: true,
      success: false,
      message: this.translate.instant("guestUpgrade.errors.moveGuestStateFailed"),
      error
    };
  }

  private failedResult(type: "guest-owner" | "public-invite", error: any): GuestUpgradeClaimResult {
    const staleStateCleared = this.clearStaleLocalClaimState(type, error);
    return {
      attempted: true,
      success: false,
      type,
      message: this.failureMessage(type, error),
      error,
      staleStateCleared
    };
  }

  private failureMessage(type: "guest-owner" | "public-invite", error: any): string {
    const status = error instanceof HttpErrorResponse || error && error.status ? error.status : null;
    if (status === 409) {
      return type === "guest-owner"
        ? "This match was already claimed or can no longer be claimed."
        : "This RSVP was already claimed by another profile or can no longer be claimed.";
    }

    if (status === 403) {
      return type === "guest-owner"
        ? this.translate.instant("guestUpgrade.errors.guestOwnerSessionInvalid")
        : this.translate.instant("guestUpgrade.errors.publicInviteClaimInvalid");
    }

    if (status === 404) {
      return type === "guest-owner"
        ? this.translate.instant("guestUpgrade.errors.guestOwnerMatchMissing")
        : this.translate.instant("guestUpgrade.errors.publicInviteClaimMissing");
    }

    return this.translate.instant("guestUpgrade.errors.moveGuestStateFailed");
  }

  private clearStaleLocalClaimState(type: "guest-owner" | "public-invite", error: any): boolean {
    const status = error instanceof HttpErrorResponse || error && error.status ? error.status : null;
    if (status !== 403 && status !== 404 && status !== 409) {
      return false;
    }

    if (type === "guest-owner") {
      this.guestOwnerSessionService.clearSession();
      return true;
    }

    const claim = this.firstPublicInviteClaim();
    if (claim) {
      this.clearPublicInviteParticipantClaim(claim.inviteToken);
      return true;
    }

    return false;
  }

  private isTerminalPrivateTokenError(error: any): boolean {
    const status = error instanceof HttpErrorResponse || error && error.status ? error.status : null;
    return status === 403 || status === 404 || status === 409;
  }

  private firstPublicInviteClaim(): PublicInviteParticipantClaimState {
    const claims = this.readPublicInviteClaims();
    const inviteTokens = Object.keys(claims)
      .filter((inviteToken) => this.isValidPublicInviteClaim(claims[inviteToken]));
    if (inviteTokens.length === 0) {
      return null;
    }

    return inviteTokens
      .map((inviteToken) => claims[inviteToken])
      .sort((first, second) => (second.updatedAt || "").localeCompare(first.updatedAt || ""))[0];
  }

  private clearPublicInviteParticipantClaim(inviteToken: string): void {
    const claims = this.readPublicInviteClaims();
    delete claims[inviteToken];
    this.writePublicInviteClaims(claims);
  }

  private readPublicInviteClaims(): {[inviteToken: string]: PublicInviteParticipantClaimState} {
    const rawClaims = localStorage.getItem(GuestUpgradeClaimService.PUBLIC_INVITE_CLAIMS_KEY);
    if (!rawClaims) {
      return {};
    }

    try {
      return JSON.parse(rawClaims) || {};
    } catch (_) {
      localStorage.removeItem(GuestUpgradeClaimService.PUBLIC_INVITE_CLAIMS_KEY);
      return {};
    }
  }

  private writePublicInviteClaims(claims: {[inviteToken: string]: PublicInviteParticipantClaimState}): void {
    const inviteTokens = Object.keys(claims)
      .filter((inviteToken) => this.isValidPublicInviteClaim(claims[inviteToken]));
    if (inviteTokens.length === 0) {
      localStorage.removeItem(GuestUpgradeClaimService.PUBLIC_INVITE_CLAIMS_KEY);
      return;
    }

    const validClaims = inviteTokens.reduce((accumulator: {[inviteToken: string]: PublicInviteParticipantClaimState}, inviteToken) => {
      accumulator[inviteToken] = claims[inviteToken];
      return accumulator;
    }, {});
    localStorage.setItem(GuestUpgradeClaimService.PUBLIC_INVITE_CLAIMS_KEY, JSON.stringify(validClaims));
  }

  private isValidPublicInviteClaim(claim: PublicInviteParticipantClaimState): boolean {
    return !!(claim && claim.inviteToken && claim.participantId && claim.guestParticipantToken && claim.eventId);
  }
}
